import React, { useEffect, useState, useRef } from 'react';
import './ChatSupport.css';
import ScrollIndicator from '../ScrollIndicator';
import TearFaceIcon from '../TearFaceIcon';
import { useAnalytics } from '../../utils/analytics/useAnalytics';

const sections = [
  { id: 'chat-hero-section', label: 'Overview' },
  { id: 'chat-comparison-section', label: 'Model Trials' },
  { id: 'chat-architecture-section', label: 'Architecture' },
  { id: 'chat-caveats-section', label: 'Caveats' },
  { id: 'chat-demo-section', label: 'Live Demo' },
  { id: 'chat-use-cases-section', label: 'Use Cases' }
];

const ChatSupport = () => {
  const { track, trackButton } = useAnalytics();
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [webGPUStatus, setWebGPUStatus] = useState('checking'); // 'checking', 'supported', 'not-supported'
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const scriptInjected = useRef(false);

  // Check WebGPU support on mount
  useEffect(() => {
    if (!navigator.gpu) {
      setWebGPUStatus('not-supported');
      return;
    }

    navigator.gpu.requestAdapter()
      .then(adapter => {
        setWebGPUStatus(adapter ? 'supported' : 'not-supported');
      })
      .catch(() => {
        setWebGPUStatus('not-supported');
      });
  }, []);

  // Track progress from the widget
  useEffect(() => {
    const handleProgress = (e) => {
      const { status, progress, message, error } = (e && e.detail) || {};
      
      if (status === 'loading') {
        setDownloadProgress(progress || 0);
      } else if (status === 'ready') {
        setIsReady(true);
        setDownloadProgress(100);
      } else if (status === 'error') {
        setLoadError(error || message || 'Failed to load model');
      }
    };

    window.addEventListener('chatModelLoadingProgress', handleProgress);
    return () => window.removeEventListener('chatModelLoadingProgress', handleProgress);
  }, []);

  // Track page view
  useEffect(() => {
    track('page_view', {
      category: 'ChatSupport',
      label: isDemoMode ? 'Charlie\'s Cleaners Demo' : 'Chat Support Page'
    });
  }, [track, isDemoMode]);

  const handleStartDemo = () => {
    if (webGPUStatus === 'not-supported') {
      alert('WebGPU is not supported in your current browser. Please use Chrome, Edge, or a compatible version of Safari/Firefox.');
      return;
    }

    setIsTransitioning(true);
    trackButton('Try Live Demo', 'chat-support');
    
    // Switch content halfway through animation
    setTimeout(() => {
      setIsDemoMode(true);
      window.scrollTo(0, 0);
    }, 700);

    // End transition state
    setTimeout(() => {
      setIsTransitioning(false);
    }, 1400);
  };

  const handleCloseDemo = () => {
    setIsDemoMode(false);
    setIsReady(false);
    setDownloadProgress(0);
    setLoadError(null);
    window.scrollTo(0, 0);
  };

  // Load script ONCE — only when demo first activates

  useEffect(() => {
    if (!isDemoMode) return;
    if (scriptInjected.current) {
      // Already loaded — just show it and re-init
      const container = document.querySelector('.chatsupportWidgetContainer');
      if (container) container.style.display = '';
      if (window.ChatSupportWidget?.instance?.chatEngine) {
        window.ChatSupportWidget.instance.chatEngine.initialize();
      }
      return;
    }

    scriptInjected.current = true;
    const script = document.createElement('script');
    script.id = 'tf-chatsupport-widget';
    script.src = '/widgets/chatsupport/chatsupport-widget.min.js';
    script.setAttribute('data-api-key', 'charlies-cleaners-demo');
    script.setAttribute('data-size', 'M');
    script.setAttribute('data-corner', 'bottomRight');
    script.setAttribute('data-theme', 'light');
    script.setAttribute('data-color', '#133e70');
    script.onload = () => {
      if (window.ChatSupportWidget?.instance?.chatEngine) {
        window.ChatSupportWidget.instance.chatEngine.initialize();
      }
    };
    document.body.appendChild(script);
  }, [isDemoMode]);

  // Hide (not remove) when exiting demo
  useEffect(() => {
    if (isDemoMode) return;
    const container = document.querySelector('.chatsupportWidgetContainer');
    if (container) container.style.display = 'none';
  }, [isDemoMode]);

  // Hide on component unmount
  useEffect(() => {
    return () => {
      const container = document.querySelector('.chatsupportWidgetContainer');
      if (container) container.style.display = 'none';
    };
  }, []);

  if (isDemoMode) {
    return (
      <div className={`charlies-demo-mode ${isTransitioning ? 'transitioning' : ''}`}>
        <div className="iris-wipe"></div>
        <TearFaceIcon />
        
        <button className="exit-demo-btn" onClick={handleCloseDemo}>
          Exit Demo
        </button>

        <div className="charlies-content">
          <main className="charlies-hero">
            <div className="hero-prompt-container">
              <h3 className="prompt-title">
                {loadError ? (
                  <span className="error-text">AI Initialization Error</span>
                ) : !isReady ? (
                  `Preparing Private AI... ${downloadProgress}%`
                ) : (
                  "Try Asking the AI:"
                )}
              </h3>
              
              <div className="prompt-bubbles">
                <button className="prompt-bubble" onClick={() => {
                  if (window.ChatSupportWidget?.instance?.panel?.messageInput) {
                    window.ChatSupportWidget.instance.panel.messageInput.value = "What are your hours?";
                    window.ChatSupportWidget.instance.panel.handleSendMessage();
                  } else if (window.ChatSupportWidget?.instance) {
                    window.ChatSupportWidget.instance.togglePanel();
                    // Small delay to let panel open before setting value
                    setTimeout(() => {
                      if (window.ChatSupportWidget.instance.panel?.messageInput) {
                        window.ChatSupportWidget.instance.panel.messageInput.value = "What are your hours?";
                        window.ChatSupportWidget.instance.panel.handleSendMessage();
                      }
                    }, 500);
                  }
                }}>
                  "What are your hours?"
                </button>
                <button className="prompt-bubble" onClick={() => {
                  if (window.ChatSupportWidget?.instance?.panel?.messageInput) {
                    window.ChatSupportWidget.instance.panel.messageInput.value = "Do you offer same-day service?";
                    window.ChatSupportWidget.instance.panel.handleSendMessage();
                  } else if (window.ChatSupportWidget?.instance) {
                    window.ChatSupportWidget.instance.togglePanel();
                    setTimeout(() => {
                      if (window.ChatSupportWidget.instance.panel?.messageInput) {
                        window.ChatSupportWidget.instance.panel.messageInput.value = "Do you offer same-day service?";
                        window.ChatSupportWidget.instance.panel.handleSendMessage();
                      }
                    }, 500);
                  }
                }}>
                  "Do you offer same-day service?"
                </button>
                <button className="prompt-bubble" onClick={() => {
                  if (window.ChatSupportWidget?.instance?.panel?.messageInput) {
                    window.ChatSupportWidget.instance.panel.messageInput.value = "How do I schedule a pickup?";
                    window.ChatSupportWidget.instance.panel.handleSendMessage();
                  } else if (window.ChatSupportWidget?.instance) {
                    window.ChatSupportWidget.instance.togglePanel();
                    setTimeout(() => {
                      if (window.ChatSupportWidget.instance.panel?.messageInput) {
                        window.ChatSupportWidget.instance.panel.messageInput.value = "How do I schedule a pickup?";
                        window.ChatSupportWidget.instance.panel.handleSendMessage();
                      }
                    }, 500);
                  }
                }}>
                  "How do I schedule a pickup?"
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-support-container ${isTransitioning ? 'transitioning' : ''}`}>
      <div className="iris-wipe"></div>
      <TearFaceIcon />
      
      <ScrollIndicator sections={sections} />
      
      <div className="chat-support-page">
        {/* Hero Section */}
        <div className='chat-section chat-hero-section' id='chat-hero-section'>
          <div className='chat-hero-content'>
            <div className='chat-hero-text'>
              <h1>Private Chat Support<span className='comma'>,</span><br/>
                 Browser-Native AI<span className='dot'>.</span>
              </h1>
              <div className='chat-abstract'>
                <p>
                  We explored the feasibility of running large-scale language models entirely within the browser. 
                  By leveraging Transformers.js and ONNX Runtime, we successfully implemented a private-by-design 
                  chat assistant that performs complex inference on the client side, removing the need for 
                  centralized AI servers.
                </p>
                <div className='chat-keywords'>
                  <span>Keywords:</span> Transformers.js, Edge AI, Privacy-First, WebAssembly, Client-Side Inference
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Trials Comparison Section */}
        <div className='chat-section chat-comparison-section' id='chat-comparison-section'>
          <h2>Model Trials & Selection</h2>
          <div className='methodology-grid'>
            <div className='methodology-card'>
              <h3>Phi-3.5-mini-instruct (2.2GB)</h3>
              <p><strong>Our optimal selection.</strong> While the largest model tested, it provided the most reliable RAG adherence and reasoning depth. It followed instructions strictly without "drifting" during long conversations.</p>
              <div className='tech-details'>
                <strong>RAG Adherence:</strong> Good
              </div>
            </div>
            <div className='methodology-card'>
              <h3>Llama-3.2-1B-Instruct (~1GB)</h3>
              <p>Impressive performance-to-size ratio. However, it exhibited a "forgetfulness" trait in multi-turn RAG scenarios, sometimes losing track of the provided knowledge base mid-conversation.</p>
              <div className='tech-details'>
                <strong>RAG Adherence:</strong> Moderate
              </div>
            </div>
            <div className='methodology-card'>
              <h3>Qwen2.5-0.5B-Instruct (~350MB)</h3>
              <p>The fastest download footprint. Unfortunately, it suffered from high hallucination rates and often bypassed the provided knowledge base in favor of its pre-trained "creative" weights.</p>
              <div className='tech-details'>
                <strong>RAG Adherence:</strong> Poor
              </div>
            </div>
          </div>
        </div>

        {/* Technical Architecture Section */}
        <div className='chat-section chat-architecture-section' id='chat-architecture-section'>
          <h2>Technical Architecture</h2>
          <div className='implementation-details'>
            <div className='tech-specs'>
              <ul>
                <li><strong>Edge Inference:</strong> Built on Transformers.js and ONNX Runtime leveraging WebGPU hardware acceleration.</li>
                <li><strong>Retrieval Strategy:</strong> Optimized build-time TF-IDF indexing for high-precision local knowledge retrieval.</li>
                <li><strong>UI Integration:</strong> Multi-threaded Web Workers combined with a custom real-time TextStreamer interface.</li>
                <li><strong>Model Precision:</strong> 4-bit (q4f16) weight quantization to balance memory pressure with reasoning accuracy.</li>
              </ul>
              
              <div className='performance-metrics'>
                <div className='metric-grid'>
                  <div className='metric'>
                    <span className='metric-value'>2.2GB</span>
                    <span className='metric-label'>Model Weight</span>
                  </div>
                  <div className='metric'>
                    <span className='metric-value'>WebGPU</span>
                    <span className='metric-label'>Acceleration</span>
                  </div>
                  <div className='metric'>
                    <span className='metric-value'>100%</span>
                    <span className='metric-label'>Privacy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Caveats Section */}
        <div className='chat-section chat-caveats-section' id='chat-caveats-section'>
          <h2>Technical Caveats</h2>
          <div className='implementation-details'>
            <div className='tech-specs'>
              <ul>
                <li><strong>The 2.2GB Barrier:</strong> Initial download latency remains the primary UX hurdle, framing this as a power-user or enterprise edge solution rather than a general consumer feature.</li>
                <li><strong>Browser Cache Bloat:</strong> Persistent model storage aggressively consumes IndexedDB/LocalStorage space, which can trigger system storage warnings on mobile or lower-tier hardware.</li>
                <li><strong>Memory Pressure:</strong> Running high-fidelity inference in a shared browser environment can lead to tab instability or "Out of Memory" crashes on devices with limited RAM.</li>
                <li><strong>WebGPU Fragmentation:</strong> While hardware acceleration is transformative, browser support and driver compatibility remain fragmented across different operating systems and mobile devices.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Live Demo Section */}
        <div className='chat-section chat-demo-section' id='chat-demo-section'>
          <h2>See It In Action</h2>
          <p className="demo-intro">Experience this implementation in a real-world scenario. Try our high-fidelity "Charlie's Cleaners" demo.</p>
          <div className="demo-trigger-container">
            <button className="try-demo-btn" onClick={handleStartDemo}>
              Try Live Demo
            </button>
          </div>
        </div>

        {/* Use Cases Section */}
        <div className='chat-section chat-use-cases-section' id='chat-use-cases-section'>
          <h2>Use Cases</h2>
          <div className='chat-use-cases-grid'>
            <div className='chat-use-case-card'>
              <h3>Privacy-First</h3>
              <p>Critical for Healthcare, Legal, and Finance. Sensitive data never leaves the device.</p>
            </div>
            <div className='chat-use-case-card'>
              <h3>Always Available</h3>
              <p>Reliable support for maritime, remote field, or restricted government workstations.</p>
            </div>
            <div className='chat-use-case-card'>
              <h3>Zero-Cost Scaling</h3>
              <p>Replace expensive cloud API calls for FAQs with free, high-performance edge inference.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSupport;
